/**
 * CalibrationReferencePage — the "Why TurfPulse behaves the way it does"
 * doc, with side-by-side founder-baseline vs your-yard data.
 *
 * Renders three sections:
 *   1. THE FOUNDER'S BASELINE — read-only, sourced from
 *      `/api/moisture-calibration/baseline`. This is the canonical
 *      calibration that seeds every new user.
 *   2. YOUR ZONES — per-zone constants the rule engine uses for YOUR
 *      yard. Sourced from `/api/moisture-calibration/zones`; auto-seeded
 *      from baseline if the user has no fire history yet, then auto-
 *      tuned via `POST /api/moisture-calibration/auto-tune`.
 *   3. THE RULE LIBRARY — all 7 rules with founder findings and current
 *      enabled state.
 *
 * Reachable from the dashboard Settings nav + a deep link from the
 * Moisture Alerts rules page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, BookOpen, Sparkles, Loader2, Activity, CheckCircle2, TrendingUp,
} from 'lucide-react';
import { API_URL } from '../../config/api';

const API = API_URL;

const SEASON_TONES = {
  Winter: 'bg-sky-500/15 text-sky-300 border-sky-700/40',
  Spring: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  Summer: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  Fall:   'bg-rose-500/15 text-rose-300 border-rose-700/40',
};

function DriftSparkline({ history }) {
  if (!history || history.length < 2) {
    return (
      <div className="text-[10px] text-slate-500 italic">
        Drift chart appears after 2+ auto-tunes (run more zones first).
      </div>
    );
  }
  const vals = history.map((h) => Number(h.minutes_per_pct));
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = Math.max(0.001, hi - lo);
  const w = 260, h = 36, padX = 4, padY = 4;
  const stepX = (w - padX * 2) / Math.max(1, history.length - 1);
  const points = history.map((rec, i) => {
    const x = padX + stepX * i;
    const y = h - padY - ((Number(rec.minutes_per_pct) - lo) / span) * (h - padY * 2);
    return [x, y, rec];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <path d={path} fill="none" stroke="#34d399" strokeWidth="1.6" strokeLinecap="round" />
      {points.map(([x, y, rec], i) => (
        <circle key={i} cx={x} cy={y} r="2.2" fill="#34d399">
          <title>{`${rec.season} · ${Number(rec.minutes_per_pct).toFixed(2)} min/% · ${rec.sample_count} samples`}</title>
        </circle>
      ))}
    </svg>
  );
}

function DriftPanel({ zoneId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    if (history) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/moisture-calibration/zones/${encodeURIComponent(zoneId)}/history?limit=24`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!open) loadHistory();
    setOpen(!open);
  };

  // Compute season-by-season pills from history (Spring: 1.0 → Summer: 0.85 …)
  const seasonPills = (() => {
    if (!history || history.length === 0) return [];
    const bySeason = {};
    for (const rec of history) {
      bySeason[rec.season] = rec; // keep the latest per season
    }
    const order = ['Winter', 'Spring', 'Summer', 'Fall'];
    return order
      .map((s) => bySeason[s])
      .filter(Boolean);
  })();

  return (
    <div className="border-t border-slate-800 pt-2">
      <button
        type="button"
        onClick={toggle}
        data-testid={`drift-toggle-${zoneId}`}
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-200"
      >
        <TrendingUp className="w-3 h-3" />
        Drift over time
        <span className="text-slate-600">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div
          data-testid={`drift-panel-${zoneId}`}
          className="mt-2 bg-slate-950/60 rounded-lg p-2.5"
        >
          {loading && <div className="text-[10px] text-slate-500 animate-pulse">Loading history…</div>}
          {!loading && history && history.length === 0 && (
            <div className="text-[10px] text-slate-500 italic leading-snug">
              No tuning history yet. Each time you run "Auto-tune from my
              fire history" with new samples, a snapshot is saved here —
              so you can see how this zone changes across seasons.
            </div>
          )}
          {!loading && history && history.length > 0 && (
            <>
              <DriftSparkline history={history} />
              {seasonPills.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {seasonPills.map((rec, i) => (
                    <span
                      key={i}
                      className={`text-[9px] px-1.5 py-0.5 rounded-full border ${SEASON_TONES[rec.season] || 'bg-slate-700/40 text-slate-300 border-slate-700/40'}`}
                    >
                      {rec.season}: {Number(rec.minutes_per_pct).toFixed(2)}
                    </span>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-2 leading-snug">
                {history.length} auto-tune snapshot{history.length === 1 ? '' : 's'} on file.
                Weather + soil seasoning shift this number — that's why
                we re-tune from your real fire data instead of locking it.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PersonalityBadge({ p }) {
  const cfg = {
    control: { label: 'Control', cls: 'bg-amber-500/20 text-amber-300 border-amber-700/50' },
    overwater_risk: { label: 'Overwater Risk', cls: 'bg-sky-500/20 text-sky-300 border-sky-700/50' },
    priority: { label: 'Priority', cls: 'bg-rose-500/20 text-rose-300 border-rose-700/50' },
    custom: { label: 'Custom', cls: 'bg-slate-700/40 text-slate-300 border-slate-700/50' },
  }[p] || { label: p, cls: 'bg-slate-800 text-slate-300 border-slate-700/50' };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function ZoneRow({ z, isBaseline = false }) {
  return (
    <div
      data-testid={`zone-row-${z.zone_id}`}
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-slate-100 text-sm">{z.zone_name || z.zone_id}</div>
        <PersonalityBadge p={z.personality} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="bg-slate-950/60 rounded-lg p-2">
          <div className="text-slate-500 text-[9px] uppercase tracking-wider">min/pct</div>
          <div className="text-emerald-400 font-bold text-base leading-none mt-1">
            {Number(z.minutes_per_pct).toFixed(2)}
          </div>
        </div>
        <div className="bg-slate-950/60 rounded-lg p-2">
          <div className="text-slate-500 text-[9px] uppercase tracking-wider">in rain/pct</div>
          <div className="text-emerald-400 font-bold text-base leading-none mt-1">
            {Number(z.rain_in_per_pct).toFixed(3)}
          </div>
        </div>
        <div className="bg-slate-950/60 rounded-lg p-2">
          <div className="text-slate-500 text-[9px] uppercase tracking-wider">% drop/day</div>
          <div className="text-emerald-400 font-bold text-base leading-none mt-1">
            {Number(z.dry_down_pct_per_day).toFixed(1)}
          </div>
        </div>
      </div>
      {!isBaseline && z.auto_tuned && (
        <div className="flex items-center gap-1 text-[10px] text-emerald-400">
          <CheckCircle2 className="w-3 h-3" />
          Auto-tuned from {z.sample_count} verified run{z.sample_count === 1 ? '' : 's'}
        </div>
      )}
      {(isBaseline ? z.founder_note : null) && (
        <div className="text-[11px] text-slate-400 leading-snug border-t border-slate-800 pt-2">
          {z.founder_note}
        </div>
      )}
      {!isBaseline && <DriftPanel zoneId={z.zone_id} />}
    </div>
  );
}

function RuleCard({ rule }) {
  return (
    <div
      data-testid={`rule-doc-${rule.id}`}
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-3"
    >
      <div className="flex items-start gap-2">
        <div className="text-2xl leading-none">{rule.emoji}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Rule {rule.order}
          </div>
          <div className="font-semibold text-slate-100 text-sm">{rule.name}</div>
          <div className="text-[12px] text-slate-300 mt-1.5 leading-snug">{rule.summary}</div>
          <div className="text-[11px] text-slate-500 mt-2 leading-snug">
            <b className="text-slate-400">Trigger:</b> {rule.trigger}
          </div>
          <div className="text-[11px] text-emerald-400/80 mt-1.5 leading-snug italic">
            {rule.founder_finding}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CalibrationReferencePage() {
  const navigate = useNavigate();
  const [baseline, setBaseline] = useState(null);
  const [zones, setZones] = useState(null);
  const [tuning, setTuning] = useState(false);
  const [tuneResult, setTuneResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [bRes, zRes] = await Promise.all([
        fetch(`${API}/api/moisture-calibration/baseline`, { credentials: 'include' }),
        fetch(`${API}/api/moisture-calibration/zones`, { credentials: 'include' }),
      ]);
      if (!bRes.ok) throw new Error(`baseline HTTP ${bRes.status}`);
      if (!zRes.ok) throw new Error(`zones HTTP ${zRes.status}`);
      setBaseline(await bRes.json());
      setZones(await zRes.json());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runAutoTune = async () => {
    setTuning(true);
    setTuneResult(null);
    try {
      const res = await fetch(`${API}/api/moisture-calibration/auto-tune`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTuneResult(data);
      await fetchAll(); // refresh the displayed zones
    } catch (e) {
      setTuneResult({ error: e.message });
    } finally {
      setTuning(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-slate-100 pb-24">
      <div className="max-w-md mx-auto px-4 pt-3">
        {/* Header */}
        <div className="flex items-center justify-between py-2">
          <button
            data-testid="calref-back"
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800"
          >
            <ChevronLeft className="w-6 h-6 text-slate-200" />
          </button>
          <div className="text-base font-semibold tracking-tight flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-emerald-400" /> Calibration Reference
          </div>
          <span className="w-9" />
        </div>

        <p className="text-xs text-slate-400 mb-4 leading-snug px-1">
          The data that makes TurfPulse smarter than schedule-based irrigation.
          Every rule + constant on this page was anchored by the founder's
          own yard, then auto-tunes to <i>your</i> yard from each verified fire.
        </p>

        {error && (
          <div data-testid="calref-error" className="bg-rose-950/40 border border-rose-900 rounded-xl p-3 text-xs text-rose-300 mb-4">
            Failed to load: {error}
          </div>
        )}

        {/* Section 1 — Founder's Baseline */}
        {baseline && (
          <section data-testid="section-baseline" className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">
                The Founder's Baseline
              </h2>
              <span className="text-[10px] text-slate-500 font-mono">{baseline.version}</span>
            </div>
            <div className="text-[11px] text-slate-400 mb-2 leading-snug">
              {baseline.source}
            </div>
            <div className="space-y-2">
              {baseline.zones.map((z) => (
                <ZoneRow key={z.zone_role} z={{
                  ...z,
                  zone_id: z.zone_role,
                  zone_name: z.name,
                }} isBaseline />
              ))}
            </div>
          </section>
        )}

        {/* Section 2 — Your zones */}
        {zones && (
          <section data-testid="section-your-zones" className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">
                Your Yard's Calibration
              </h2>
              {zones.seeded_from_baseline ? (
                <span className="text-[10px] text-amber-400">From baseline</span>
              ) : (
                <span className="text-[10px] text-emerald-400">Auto-tuned</span>
              )}
            </div>
            <div className="space-y-2 mb-3">
              {zones.zones.map((z) => (
                <ZoneRow key={z.zone_id} z={z} />
              ))}
            </div>
            <button
              data-testid="calref-autotune"
              onClick={runAutoTune}
              disabled={tuning}
              className="w-full px-3 py-2.5 rounded-lg bg-emerald-700/80 text-white text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {tuning ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Auto-tuning from fire history…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Auto-tune from my fire history</>
              )}
            </button>
            {tuneResult && (
              <div
                data-testid="calref-tune-result"
                className="mt-2 bg-slate-900/80 border border-slate-800 rounded-xl p-3 text-xs"
              >
                {tuneResult.error ? (
                  <div className="text-rose-400">Failed: {tuneResult.error}</div>
                ) : tuneResult.tuned?.length ? (
                  <>
                    <div className="text-emerald-400 font-semibold mb-1">
                      Tuned {tuneResult.tuned.length} zone{tuneResult.tuned.length === 1 ? '' : 's'} from {tuneResult.samples} sample{tuneResult.samples === 1 ? '' : 's'}.
                    </div>
                    {tuneResult.tuned.map((t, i) => (
                      <div key={i} className="text-slate-400 mt-0.5">
                        • <b className="text-slate-200">{t.zone_id}</b>: {Number(t.minutes_per_pct).toFixed(3)} min/% (median {Number(t.median).toFixed(3)}) · {t.sample_count} runs
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-slate-400 leading-snug">
                    {tuneResult.reason === 'no_runs_in_30d'
                      ? 'No verified fires in the last 30 days yet. Run a couple of zones (or wait for a scheduled fire) and try again.'
                      : 'Not enough confident samples (need ≥ 2 per zone with delta between 2-30%).'}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Section 3 — The rule library */}
        {baseline && baseline.rules && (
          <section data-testid="section-rules" className="mb-6">
            <h2 className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2">
              The Rule Library
            </h2>
            <p className="text-[11px] text-slate-400 mb-3 leading-snug">
              Seven rules that turn raw probe data into closed-loop irrigation.
              Italic lines below each rule are what the founder verified
              in his own yard — not assumptions, not docs, real measurements.
            </p>
            <div className="space-y-2">
              {baseline.rules.map((r) => (
                <RuleCard key={r.id} rule={r} />
              ))}
            </div>
          </section>
        )}

        <div className="text-[10px] text-slate-600 text-center mt-6 px-3">
          <Activity className="w-3 h-3 inline-block mr-1 -mt-0.5" />
          This page is the canonical TurfPulse calibration reference.
          When a new user signs up, these values seed their first run.
        </div>
      </div>
    </div>
  );
}
